import { VectorTile } from '@mapbox/vector-tile';
import { PbfReader } from 'pbf';

export { VectorTile, PbfReader };

type FeatureKind = 'water' | 'river' | 'canal' | 'stream' | 'forest' | 'marsh' | 'sand' | 'grass';

type CoverFlag = 'water' | 'river' | 'forest' | 'marsh' | 'road' | 'bridge' | 'sand' | 'grass';

export interface TerrainRules {
    slope_span_m: number;
    hill_slope_deg: number;
    mountain_slope_deg: number;
    high_mountain_slope_deg: number;
    ocean_void_m: number;
    river_mark_m: number;
    road_mark_m: number;
    flags: Record<CoverFlag, number>;
    dry_depressions: [number, number, number, number][];
}

interface FeatureSource {
    tile: FeatureTile;
    z: number;
    x: number;
    y: number;
}

interface FeatureGroup {
    kind: FeatureKind;
    path: Path2D;
    extent: number;
    tunnel: boolean;
    intermittent: boolean;
}

interface FeatureTile {
    groups: FeatureGroup[];
    counts: { water: number; rivers: number };
}

interface Geography {
    WORLD: number;
    ORIGIN: number;
}

interface FeatureView {
    mx: number;
    my: number;
    zoom: number;
    width: number;
    height: number;
}

interface TilePlacement {
    z: number;
    x: number;
    y: number;
    key: string;
    left: number;
    top: number;
    size: number;
}

interface FeatureLayers {
    water: boolean;
}

const MAX_ZOOM = 14;
const MAX_TILES = 96;
const RETRY_MS = 8000;
const LANDCOVER = new Set(['forest', 'marsh', 'sand', 'grass']);
const ORDER: Record<FeatureKind, number> = {
    forest: -4, sand: -3, grass: -2, marsh: -1,
    stream: 1, canal: 2, river: 3, water: 4,
};

export function isTerrainRules(value: unknown): value is TerrainRules {
    if (!value || typeof value !== 'object') return false;
    const numberFields = ['slope_span_m', 'hill_slope_deg', 'mountain_slope_deg',
        'high_mountain_slope_deg', 'ocean_void_m', 'river_mark_m', 'road_mark_m'];
    const entries = new Map<string, unknown>(Object.entries(value));
    const numbers = numberFields.map(key => entries.get(key));
    if (!numbers.every((number): number is number => typeof number === 'number' && Number.isFinite(number))) return false;
    const [span, hill, mountain, peak, , river, road] = numbers;
    if (span <= 0 || river <= 0 || road <= 0 || hill < 0 || mountain <= hill || peak <= mountain || peak >= 90) return false;
    const flags: unknown = entries.get('flags');
    if (!flags || typeof flags !== 'object') return false;
    const flagEntries = new Map(Object.entries(flags));
    const names: CoverFlag[] = ['water', 'river', 'forest', 'marsh', 'road', 'bridge', 'sand', 'grass'];
    if (!names.every(key => {
        const flag: unknown = flagEntries.get(key);
        return typeof flag === 'number' && Number.isInteger(flag) && flag > 0 && flag <= 128 && (flag & (flag - 1)) === 0;
    }) || new Set(names.map(key => flagEntries.get(key))).size !== names.length) return false;
    const depressions: unknown = entries.get('dry_depressions');
    return Array.isArray(depressions) && depressions.every(box => Array.isArray(box) && box.length === 4
        && box.every(coordinate => typeof coordinate === 'number' && Number.isFinite(coordinate))
        && box[0] <= box[2] && box[1] <= box[3]);
}

export function normalizeCampaignTerrain(terrain: number): number {
    return terrain === 4 ? 0 : terrain;
}

export function classifyTerrain(elevation: number, slope: number, lon: number, lat: number, cover: number, rules: TerrainRules): number {
    const f = rules.flags;
    const dry = rules.dry_depressions.some(([west, south, east, north]) => west <= lon && lon <= east && south <= lat && lat <= north);
    if (cover & f.bridge) return 0;
    if (elevation <= rules.ocean_void_m && !dry) return 11;
    if (cover & f.water) return 8;
    if (cover & f.river) return 7;
    if (cover & f.marsh) return 6;
    if (cover & f.road) return 0;
    if (slope >= rules.high_mountain_slope_deg) return 10;
    if (slope >= rules.mountain_slope_deg) return 9;
    if (cover & f.forest) return 1;
    if (cover & f.sand) return elevation >= 0 && elevation <= 2 ? 5 : 3;
    if (cover & f.grass) return 4;
    if (slope >= rules.hill_slope_deg) return 2;
    if (dry && Math.abs(lat) < 35) return 3;
    return 0;
}

export function decodeFeatureTile(bytes: Uint8Array): FeatureTile {
    const tile = new VectorTile(new PbfReader(bytes));
    const groups = new Map<string, FeatureGroup>();
    const counts = { water: 0, rivers: 0 };
    for (const name of ['landcover', 'landuse', 'water', 'waterway']) {
        const layer = tile.layers[name];
        if (!layer) continue;
        for (let i = 0; i < layer.length; i++) {
            const feature = layer.feature(i);
            const props = feature.properties;
            const cls = String(props.class || '');
            let kind: FeatureKind;
            if ((name === 'landcover' || name === 'landuse') && feature.type === 3) {
                if (cls === 'wood' || cls === 'forest') kind = 'forest';
                else if (cls === 'wetland') kind = 'marsh';
                else if (cls === 'sand' || cls === 'beach') kind = 'sand';
                else if (cls === 'grass' || cls === 'grassland' || cls === 'meadow') kind = 'grass';
                else continue;
            } else if (name === 'water' && feature.type === 3 && cls !== 'ocean' && props.brunnel !== 'tunnel') {
                kind = 'water';
                counts.water++;
            } else if (name === 'waterway' && feature.type === 2
                && (cls === 'river' || cls === 'canal' || cls === 'stream')) {
                kind = cls;
                counts.rivers++;
            } else {
                continue;
            }
            const key = `${kind}/${layer.extent}/${props.brunnel || ''}/${props.intermittent || 0}`;
            let group = groups.get(key);
            if (!group) {
                group = {
                    kind, path: new Path2D(), extent: layer.extent,
                    tunnel: props.brunnel === 'tunnel',
                    intermittent: props.intermittent === 1,
                };
                groups.set(key, group);
            }
            for (const line of feature.loadGeometry()) {
                if (!line.length) continue;
                group.path.moveTo(line[0].x, line[0].y);
                for (let j = 1; j < line.length; j++) group.path.lineTo(line[j].x, line[j].y);
                if (feature.type === 3) group.path.closePath();
            }
        }
    }
    return { groups: [...groups.values()].sort((a, b) => ORDER[a.kind] - ORDER[b.kind]), counts };
}

export function featureTilePlacements(view: FeatureView, geo: Geography): TilePlacement[] {
    const z = Math.max(0, Math.min(MAX_ZOOM, Math.floor(view.zoom)));
    return tilePlacementsAtZoom(view, geo, z);
}

export function tilePlacementsAtZoom(view: FeatureView, geo: Geography, z: number, padding = 0): TilePlacement[] {
    const n = 2 ** z;
    const size = 256 * 2 ** (view.zoom - z);
    const cx = (view.mx + geo.ORIGIN) / geo.WORLD * n;
    const cy = (geo.ORIGIN - view.my) / geo.WORLD * n;
    const placements: TilePlacement[] = [];
    const minX = Math.floor(cx - view.width / (2 * size)) - padding;
    const maxX = Math.ceil(cx + view.width / (2 * size)) - 1 + padding;
    const minY = Math.max(0, Math.floor(cy - view.height / (2 * size)) - padding);
    const maxY = Math.min(n - 1, Math.ceil(cy + view.height / (2 * size)) - 1 + padding);
    for (let ty = minY; ty <= maxY; ty++) {
        for (let tx = minX; tx <= maxX; tx++) {
            const x = ((tx % n) + n) % n;
            placements.push({
                z, x, y: ty, key: `${z}/${x}/${ty}`,
                left: (tx - cx) * size + view.width / 2,
                top: (ty - cy) * size + view.height / 2,
                size,
            });
        }
    }
    return placements;
}

export class WCFeatureLayer {
    readonly tiles = new Map<string, FeatureTile>();
    readonly inflight = new Map<string, AbortController>();
    private wanted = new Set<string>();
    private queue: TilePlacement[] = [];
    private failures = new Map<string, { message: string; retryAt: number }>();
    private retryTimer: ReturnType<typeof setTimeout> | undefined;
    private running = false;
    private baseUrl = '';
    private readonly geo: Geography;
    private readonly onChange: () => void;
    private maskCanvas: HTMLCanvasElement | null = null;

    constructor(geo: Geography, onChange: () => void) {
        this.geo = geo;
        this.onChange = onChange;
    }

    setBaseUrl(baseUrl: string): void {
        baseUrl = (baseUrl || '').replace(/\/$/, '');
        if (baseUrl === this.baseUrl) return;
        const running = this.running;
        this.stop();
        this.baseUrl = baseUrl;
        this.tiles.clear();
        this.failures.clear();
        this.wanted.clear();
        if (running) this.start();
    }

    start(): void { this.running = true; }

    stop(): void {
        this.running = false;
        for (const controller of this.inflight.values()) controller.abort();
        this.inflight.clear();
        this.queue = [];
        clearTimeout(this.retryTimer);
    }

    get loading(): number {
        return this.queue.length + [...this.inflight.keys()].filter(key => this.wanted.has(key)).length;
    }

    get error(): string {
        for (const key of this.wanted) {
            const failure = this.failures.get(key);
            if (failure) return failure.message;
        }
        return '';
    }

    prepare(view: FeatureView): void {
        this.request(featureTilePlacements(view, this.geo), view);
        this.evict();
    }

    sourceFor(z: number, x: number, y: number): FeatureSource | null {
        let level = Math.min(z, MAX_ZOOM);
        x = Math.floor(x / 2 ** (z - level));
        y = Math.floor(y / 2 ** (z - level));
        while (level >= 0) {
            const key = `${level}/${x}/${y}`;
            const tile = this.tiles.get(key);
            if (tile) {
                this.tiles.delete(key);
                this.tiles.set(key, tile);
                return { tile, z: level, x, y };
            }
            level--; x = Math.floor(x / 2); y = Math.floor(y / 2);
        }
        return null;
    }

    terrainMasks(source: FeatureSource | null, z: number, x: number, y: number, cells: number,
        cellMeters: number, rules: TerrainRules, layers: FeatureLayers, padding = 1): Uint8Array {
        const width = cells + padding * 2;
        const masks = new Uint8Array(width * width);
        if (!source) return masks;
        if (!this.maskCanvas) this.maskCanvas = document.createElement('canvas');
        this.maskCanvas.width = this.maskCanvas.height = width;
        const ctx = this.maskCanvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) throw new Error('Terrain landcover rasterization requires a 2D canvas.');
        const factor = 2 ** (z - source.z);
        const left = padding - (x - source.x * factor) * cells;
        const top = padding - (y - source.y * factor) * cells;
        for (const group of source.tile.groups) {
            if (!LANDCOVER.has(group.kind) && !layers.water) continue;
            const flag = group.kind === 'water' ? rules.flags.water
                : group.kind === 'forest' ? rules.flags.forest
                : group.kind === 'marsh' ? rules.flags.marsh
                : group.kind === 'sand' ? rules.flags.sand
                : group.kind === 'grass' ? rules.flags.grass : rules.flags.river;
            ctx.setTransform(1, 0, 0, 1, 0, 0);
            ctx.clearRect(0, 0, width, width);
            const scale = cells * factor / group.extent;
            ctx.setTransform(scale, 0, 0, scale, left, top);
            ctx.fillStyle = ctx.strokeStyle = '#ffffff';
            if (group.kind === 'water' || LANDCOVER.has(group.kind)) {
                ctx.fill(group.path);
            } else {
                ctx.lineWidth = Math.max(1.25, 2 * rules.river_mark_m / cellMeters) / scale;
                ctx.lineCap = ctx.lineJoin = 'round';
                ctx.stroke(group.path);
            }
            const pixels = ctx.getImageData(0, 0, width, width).data;
            for (let i = 0; i < masks.length; i++) {
                if (pixels[i * 4 + 3] >= 128) masks[i] |= flag;
            }
        }
        return masks;
    }

    draw(ctx: CanvasRenderingContext2D, view: FeatureView, layers: FeatureLayers): void {
        const placements = layers.water ? featureTilePlacements(view, this.geo) : [];
        this.request(placements, view);
        for (const placement of placements) {
            let { z, x, y, key } = placement;
            let tile = this.tiles.get(key);
            while (!tile && z > 0) {
                z--; x = Math.floor(x / 2); y = Math.floor(y / 2);
                key = `${z}/${x}/${y}`;
                tile = this.tiles.get(key);
            }
            if (!tile) continue;
            this.tiles.delete(key);
            this.tiles.set(key, tile);
            const factor = 2 ** (placement.z - z);
            const left = placement.left - (placement.x - x * factor) * placement.size;
            const top = placement.top - (placement.y - y * factor) * placement.size;
            ctx.save();
            ctx.beginPath();
            ctx.rect(placement.left, placement.top, placement.size, placement.size);
            ctx.clip();
            ctx.translate(left, top);
            for (const group of tile.groups) {
                if (LANDCOVER.has(group.kind)) continue;
                this.drawGroup(ctx, group, placement.size * factor / group.extent, view.zoom);
            }
            ctx.restore();
        }
        this.evict();
    }

    private evict(): void {
        for (const key of this.tiles.keys()) {
            if (this.tiles.size <= MAX_TILES) break;
            if (!this.wanted.has(key)) this.tiles.delete(key);
        }
    }

    private request(placements: TilePlacement[], view: FeatureView): void {
        this.wanted = new Set(placements.map(p => p.key));
        // The proxy may already be downloading a tile. Finish at most four of
        // those instead of spawning a new set on every animation/pan frame.
        if (!placements.length) {
            for (const controller of this.inflight.values()) controller.abort();
        }
        const now = performance.now();
        const queued = new Map<string, TilePlacement>();
        for (const p of placements) {
            const failure = this.failures.get(p.key);
            if (failure && now >= failure.retryAt) this.failures.delete(p.key);
            if (!this.tiles.has(p.key) && !this.inflight.has(p.key) && !this.failures.has(p.key)) {
                queued.set(p.key, p);
            }
        }
        const distance = (p: TilePlacement) => Math.hypot(p.left + p.size / 2 - view.width / 2, p.top + p.size / 2 - view.height / 2);
        this.queue = [...queued.values()].sort((a, b) => distance(a) - distance(b));
        this.pump();
        clearTimeout(this.retryTimer);
        const retries = [...this.failures].filter(([key]) => this.wanted.has(key)).map(([, failure]) => failure.retryAt);
        if (this.running && retries.length) {
            this.retryTimer = setTimeout(this.onChange, Math.max(100, Math.min(...retries) - now));
        }
    }

    private pump(): void {
        while (this.running && this.queue.length && this.inflight.size < 4) {
            const tile = this.queue.shift();
            if (tile) void this.fetchTile(tile);
        }
    }

    private async fetchTile(tile: TilePlacement): Promise<void> {
        const controller = new AbortController();
        this.inflight.set(tile.key, controller);
        try {
            const response = await fetch(`${this.baseUrl}/api/tiles/features/${tile.key}.pbf`, { signal: controller.signal });
            if (!response.ok) {
                let message = `Map feature request failed (${response.status}).`;
                if (response.headers.get('Content-Type')?.includes('application/json')) {
                    const body: unknown = await response.json();
                    if (body && typeof body === 'object' && 'error' in body && typeof body.error === 'string') message = body.error;
                }
                throw new Error(message);
            }
            const bytes = new Uint8Array(await response.arrayBuffer());
            if (controller.signal.aborted || !this.running) return;
            this.tiles.set(tile.key, decodeFeatureTile(bytes));
            this.failures.delete(tile.key);
        } catch (error) {
            if (!controller.signal.aborted && this.running) {
                const message = error instanceof Error ? error.message : String(error);
                this.failures.set(tile.key, { message, retryAt: performance.now() + RETRY_MS });
                console.warn(`[World campaign] Feature tile ${tile.key}: ${message}`);
            }
        } finally {
            if (this.inflight.get(tile.key) === controller) this.inflight.delete(tile.key);
            this.pump();
            if (this.running) this.onChange();
        }
    }

    private drawGroup(ctx: CanvasRenderingContext2D, group: FeatureGroup, scale: number, zoom: number): void {
        ctx.save();
        ctx.scale(scale, scale);
        ctx.globalAlpha = group.tunnel ? 0.55 : 1;
        if (group.kind === 'water') {
            ctx.fillStyle = '#286e96';
            // MVT ring winding preserves islands and holes with the nonzero fill rule.
            ctx.fill(group.path);
        } else {
            const width = (group.kind === 'river' ? 1.3 : group.kind === 'canal' ? 1 : 0.65)
                + Math.max(0, zoom - 8) * 0.2;
            ctx.lineJoin = ctx.lineCap = 'round';
            ctx.setLineDash(group.tunnel || group.intermittent ? [4 / scale, 3 / scale] : []);
            ctx.strokeStyle = '#62bce5';
            ctx.lineWidth = width / scale;
            ctx.stroke(group.path);
        }
        ctx.restore();
    }
}
